import { useState } from "react";

interface CompareItem {
  id: number;
  name: string;
  url: string;
  versionA: { date: string; label: string };
  versionB: { date: string; label: string };
  changeCount: number;
  paraA: string[];
  paraB: string[];
  changes: { type: "新增" | "删除" | "改写"; text: string }[];
}

const compareData: CompareItem[] = [
  {
    id: 1,
    name: "某品牌声明原文页",
    url: "https://ssnnyw.cn/arch/compare/10456",
    versionA: { date: "2026-09-03 20:02", label: "首发版本" },
    versionB: { date: "2026-09-06 09:15", label: "当前缓存" },
    changeCount: 4,
    paraA: [
      "我方于今日收到相关反馈后，第一时间启动了内部核查，并就网络流传的若干截图与表述进行逐条核实。",
      "在此过程中，个别早期信息存在不完整或不准确的情况，对此可能给公众造成的困扰，我们深表歉意。",
      "我们已于当日暂停相关合作安排，并将对内部流程进行为期两周的全面自查与整改，具体结果将另行公告。",
    ],
    paraB: [
      "我方就近期网络关注的事项进行了内部核查，对相关表述进行了重新梳理与更正。",
      "对于信息在传播过程中出现的偏差，我们表示歉意，并感谢社会各界的监督。",
      "后续进展将根据实际情况适时向公众说明。",
    ],
    changes: [
      { type: "删除", text: "“第一时间启动”“逐条核实”“两周全面自查与整改”等具体承诺被移除。" },
      { type: "改写", text: "“深表歉意”简化为“表示歉意”，语气明显缓和。" },
      { type: "新增", text: "版本 B 增加“重新梳理与更正”等概括性表述。" },
    ],
  },
  {
    id: 2,
    name: "某访谈问答专稿页",
    url: "https://ssnnyw.cn/arch/compare/10445",
    versionA: { date: "2026-08-30 15:20", label: "首次快照" },
    versionB: { date: "2026-09-06 15:30", label: "下线前缓存" },
    changeCount: 3,
    paraA: [
      "在本次对谈中，嘉宾围绕职业选择、行业现状与个人规划分享了详细观点，并就部分争议问题进行了充分回应。",
      "以下为完整问答实录，内容已根据原意整理并保留嘉宾原话的语气与细节。",
    ],
    paraB: [
      "本页内容涉及早期非正式交流，相关信息现仅以节选形式保留。",
      "如需了解更完整的信息，请以官方正式发布渠道为准。",
    ],
    changes: [
      { type: "删除", text: "完整问答实录正文与嘉宾详细观点被移除，仅保留说明性文字。" },
      { type: "改写", text: "整页性质从“完整问答”改为“节选 + 官方渠道引导”。" },
    ],
  },
];

const legend = [
  { key: "删除", cls: "bg-primary-100 text-primary-800 ring-primary-300/50" },
  { key: "新增", cls: "bg-accent-100 text-accent-800 ring-accent-300/50" },
  { key: "改写", cls: "bg-secondary-100 text-secondary-800 ring-secondary-300/50" },
];

export default function CompareSection() {
  const [active, setActive] = useState(0);
  const item = compareData[active];

  return (
    <section id="compare" className="w-full scroll-mt-16 bg-background-100/70 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-xl">
            <p className="text-sm font-medium tracking-wide text-primary-600">DIFF · 版本对比</p>
            <h2 className="mt-3 text-2xl text-foreground-950 md:text-4xl">两个时间点并排，改动无处遁形</h2>
            <p className="mt-3 text-sm leading-6 text-foreground-700 md:text-base">
              下面的演示还原了一次真实的“删改后版本”对比，切换示例即可看到增、删、改写分别如何被标注。
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {legend.map((l) => (
              <span key={l.key} className={`whitespace-nowrap rounded-full px-3 py-1 text-xs ring-1 ${l.cls}`}>
                {l.key}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {compareData.map((c, i) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setActive(i)}
              className={`whitespace-nowrap rounded-md px-4 py-2 text-sm transition-colors ${
                active === i
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 text-foreground-700 ring-1 ring-background-300 hover:bg-secondary-100"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {[item.versionA, item.versionB].map((v, side) => (
            <div key={side} className="rounded-2xl border border-background-200 bg-background-50 p-6">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-foreground-950">
                    {side === 0 ? `版本 A · ${v.label}` : `版本 B · ${v.label}`}
                  </p>
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-foreground-500">
                    <i className="ri-time-line"></i> {v.date}
                  </p>
                </div>
                <span
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs ${
                    side === 0 ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-800"
                  }`}
                >
                  {side === 0 ? "首发" : "当前"}
                </span>
              </div>

              <div className="mt-4 space-y-3 border-t border-background-200 pt-4">
                {(side === 0 ? item.paraA : item.paraB).map((p, pi) => (
                  <p
                    key={pi}
                    className={`rounded-lg px-3 py-2 text-sm leading-6 text-foreground-800 ${
                      side === 0 && pi === 0 ? "bg-primary-50/70" : side === 1 && pi === item.paraB.length - 1 ? "bg-accent-50/70" : ""
                    }`}
                  >
                    {p}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-2xl border border-background-200 bg-background-50 p-6">
          <p className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
            <i className="ri-focus-2-line text-primary-500"></i> 本次对比检测到 {item.changeCount} 处变更
          </p>
          <ul className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            {item.changes.map((c, i) => {
              const badge =
                c.type === "删除"
                  ? "bg-primary-100 text-primary-800"
                  : c.type === "新增"
                    ? "bg-accent-100 text-accent-800"
                    : "bg-secondary-100 text-secondary-800";
              return (
                <li key={i} className="flex gap-3 rounded-lg bg-background-100 p-3.5 text-sm leading-6 text-foreground-700">
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-semibold ${badge}`}>
                    {c.type}
                  </span>
                  <span>{c.text}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}